<template>
	<div class="pageRoot">
		<h2>Graphite Render Configuration</h2>
		<div v-if="error" class="error">{{error}}</div>
		<div v-else-if="loading" class="loading"><ScaleLoader /> Loading…</div>
		<template v-else-if="sections">
			<div class="toolbar">
				<input type="button" class="saveButton" value="Save Changes" :disabled="!hasChanges" @click="SaveChanges" />
				<input type="button" value="Discard Changes" :disabled="!hasChanges" @click="DiscardChanges" />
				<input type="button" value="Edit Raw JSON" @click="OpenRawEditor" />
				<span v-if="hasChanges" class="unsavedNote">● Unsaved changes</span>
			</div>
			<p class="pageHelp">
				Click a graph's <b>Edit</b> button to open the interactive editor. Drag graphs by the ⠿ handle to reorder them or move them between sections.
				Image dimensions are chosen automatically by the viewing page, so graphs no longer define a width or height.
				Nothing is applied until you click <b>Save Changes</b>.
			</p>

			<section v-for="section in sections" :key="section.key" class="graphSection">
				<h3>{{section.title}}</h3>
				<p class="sectionHelp">{{section.help}}</p>

				<div v-if="section.template" class="serverNamesEditor">
					<div class="serverNamesLabel">Server Names:</div>
					<span v-for="(sn, index) in serverNames" :key="sn" class="serverChip">
						{{sn}}
						<a role="button" tabindex="0" class="chipRemove" :aria-label="'Remove server ' + sn"
						   @click="removeServerName(index)"
						   @keydown.enter.space.prevent="removeServerName(index)">✕</a>
					</span>
					<input type="text" class="serverNameInput" v-model="newServerName" placeholder="Add server…"
						   aria-label="New server name" @keydown.enter.prevent="addServerName" />
					<input type="button" value="Add" @click="addServerName" />
					<label class="previewServerLabel">
						Preview graphs as:
						<select v-model="previewServer">
							<option v-for="sn in serverNames" :key="sn" :value="sn">{{sn}}</option>
						</select>
					</label>
				</div>

				<draggable v-model="section.graphs"
						   group="graphiteGraphs"
						   item-key="uid"
						   handle=".dragHandle"
						   class="cardGrid"
						   v-bind="dragOptions"
						   @clone="StyleOriginPlaceholder">
					<template #item="{ element: g }">
						<div class="graphCard">
							<div class="cardHeader">
								<span class="dragHandle" title="Drag to reorder or move to another section" aria-hidden="true">⠿</span>
								<span class="cardTitle" :title="caption(g)">{{caption(g)}}</span>
							</div>
							<img :src="thumbSrc(section, g)"
								 :alt="'Graph: ' + caption(g)"
								 class="cardThumb"
								 loading="lazy"
								 @click="editGraph(section, g)" />
							<div class="cardButtons">
								<input type="button" value="Edit" @click="editGraph(section, g)" />
								<input type="button" value="Duplicate" @click="duplicateGraph(section, g)" />
								<input type="button" v-if="section.key !== 'UnusedQueryStringStorage'" value="Park"
									   title="Move to Unused Graph Storage without deleting" @click="parkGraph(section, g)" />
								<input type="button" value="Delete" @click="deleteGraph(section, g)" />
							</div>
						</div>
					</template>
				</draggable>
				<div v-if="!section.graphs.length" class="emptySection">No graphs in this section.</div>
				<div class="sectionButtons">
					<input type="button" value="+ Add Graph" @click="addGraph(section)" />
				</div>
			</section>
		</template>
	</div>
</template>

<script>
	import { GetGraphiteAdminData, SetGraphiteAdminData } from 'appRoot/api/GraphiteAdminData';
	import { ParseRenderQueryString, SerializeRenderQueryString, GetGraphCaption, CreateEmptyGraphModel, CopyGraphModel } from 'appRoot/scripts/GraphiteQuery';
	import { BuildRenderSrc } from 'appRoot/scripts/GraphiteRender';
	import { ModalDialog, ModalConfirmDialog, AceCodeEditorDialog } from 'appRoot/scripts/ModalDialog';
	import GraphiteGraphEditor from 'appRoot/vues/admin/controls/GraphiteGraphEditor.vue';
	import AbandonChangesMixin from 'appRoot/scripts/AbandonChangesMixin';
	import { DragAutoScrollOptions } from 'appRoot/scripts/SortableAutoScroll';
	import { OriginPlaceholderOptions, StyleOriginPlaceholder } from 'appRoot/scripts/SortableOriginPlaceholder';
	import draggable from 'vuedraggable';

	const SECTION_DEFS = [
		{
			key: "SingleServerQueryStrings", title: "General Status", template: false,
			help: "Shown once at the top of the user-facing Graphite page."
		},
		{
			key: "MultiServerQueryStrings", title: "Individual Server Status (per-server templates)", template: true,
			help: "Each of these graphs is rendered once per server listed below; %%ServerName%% in the graph definition is replaced with each server's name."
		},
		{
			key: "DatabaseQueryStrings", title: "Database Status", template: false,
			help: "Shown in the Database Status section of the user-facing Graphite page."
		},
		{
			key: "UnusedQueryStringStorage", title: "Unused Graph Storage", template: false,
			help: "Graphs parked here are kept in the configuration but are not shown to users."
		}
	];

	export default {
		components: { draggable, GraphiteGraphEditor },
		mixins: [AbandonChangesMixin],
		data()
		{
			return {
				error: null,
				loading: false,
				sections: null,
				serverNames: [],
				newServerName: "",
				previewServer: "",
				baseline: null,
				uidCounter: 0,
				dragOptions: Object.assign({}, DragAutoScrollOptions, OriginPlaceholderOptions)
			};
		},
		created()
		{
			this.loadConfig();
		},
		computed:
		{
			hasChanges()
			{
				if (!this.sections)
					return false;
				return this.serializeConfigJson() !== this.baseline;
			}
		},
		methods:
		{
			StyleOriginPlaceholder,
			loadConfig()
			{
				this.loading = true;
				this.error = null;
				GetGraphiteAdminData()
					.then(data =>
					{
						if (data.success)
							this.importConfigJson(data.json);
						else
							this.error = data.error;
					})
					.catch(err =>
					{
						this.error = err.message;
					})
					.finally(() =>
					{
						this.loading = false;
					});
			},
			/** Parses GraphiteConfig.json text into the editable sections model and resets the change baseline. */
			importConfigJson(json)
			{
				let config = JSON.parse(json);
				this.serverNames = Array.isArray(config.ServerNames) ? config.ServerNames.slice() : [];
				this.sections = SECTION_DEFS.map(def => ({
					key: def.key,
					title: def.title,
					help: def.help,
					template: def.template,
					graphs: (Array.isArray(config[def.key]) ? config[def.key] : []).map(qs => ({
						uid: ++this.uidCounter,
						model: ParseRenderQueryString(qs)
					}))
				}));
				if (!this.previewServer || this.serverNames.indexOf(this.previewServer) === -1)
					this.previewServer = this.serverNames.length ? this.serverNames[0] : "";
				this.baseline = this.serializeConfigJson();
			},
			/** Builds the GraphiteConfig object from the current GUI state. */
			serializeConfig()
			{
				let config = { ServerNames: this.serverNames.slice() };
				for (let section of this.sections)
					config[section.key] = section.graphs.map(g => SerializeRenderQueryString(g.model));
				return {
					SingleServerQueryStrings: config.SingleServerQueryStrings,
					ServerNames: config.ServerNames,
					MultiServerQueryStrings: config.MultiServerQueryStrings,
					DatabaseQueryStrings: config.DatabaseQueryStrings,
					UnusedQueryStringStorage: config.UnusedQueryStringStorage
				};
			},
			serializeConfigJson()
			{
				return JSON.stringify(this.serializeConfig(), null, 2);
			},
			caption(g)
			{
				let text = GetGraphCaption(g.model);
				return this.previewServer ? text.replace(/%%ServerName%%/gi, this.previewServer) : text;
			},
			thumbSrc(section, g)
			{
				return BuildRenderSrc(SerializeRenderQueryString(g.model), {
					width: 380, height: 190,
					serverName: section.template ? this.previewServer : null
				});
			},
			//////////////////////////////////////////
			// Graph operations                     //
			//////////////////////////////////////////
			editGraph(section, g)
			{
				let workingCopy = CopyGraphModel(g.model);
				ModalDialog(GraphiteGraphEditor, {
					model: workingCopy,
					isNew: false,
					isTemplate: section.template,
					serverNames: this.serverNames,
					previewServer: this.previewServer
				}, { defaultCloseOnOverlayClick: false })
					.then(result =>
					{
						if (result && result.model)
							g.model = result.model;
					});
			},
			addGraph(section)
			{
				let model = CreateEmptyGraphModel();
				ModalDialog(GraphiteGraphEditor, {
					model: model,
					isNew: true,
					isTemplate: section.template,
					serverNames: this.serverNames,
					previewServer: this.previewServer
				}, { defaultCloseOnOverlayClick: false })
					.then(result =>
					{
						if (result && result.model)
							section.graphs.push({ uid: ++this.uidCounter, model: result.model });
					});
			},
			duplicateGraph(section, g)
			{
				let index = section.graphs.indexOf(g);
				section.graphs.splice(index + 1, 0, { uid: ++this.uidCounter, model: CopyGraphModel(g.model) });
			},
			parkGraph(section, g)
			{
				let index = section.graphs.indexOf(g);
				section.graphs.splice(index, 1);
				this.sections.find(s => s.key === "UnusedQueryStringStorage").graphs.push(g);
			},
			deleteGraph(section, g)
			{
				ModalConfirmDialog("Delete the graph \"" + this.caption(g) + "\"?\n\n(Consider \"Park\" instead, which moves it to Unused Graph Storage.)", "Confirm Delete", "Delete", "Cancel")
					.then(userClickedYes =>
					{
						if (userClickedYes)
						{
							let index = section.graphs.indexOf(g);
							if (index !== -1)
								section.graphs.splice(index, 1);
						}
					});
			},
			//////////////////////////////////////////
			// Server names                         //
			//////////////////////////////////////////
			addServerName()
			{
				let name = this.newServerName.trim();
				if (!name)
					return;
				if (this.serverNames.some(sn => sn.toLowerCase() === name.toLowerCase()))
				{
					toaster.warning("Server \"" + name + "\" is already in the list.");
					return;
				}
				this.serverNames.push(name);
				this.newServerName = "";
				if (!this.previewServer)
					this.previewServer = name;
			},
			removeServerName(index)
			{
				let removed = this.serverNames.splice(index, 1)[0];
				if (this.previewServer === removed)
					this.previewServer = this.serverNames.length ? this.serverNames[0] : "";
			},
			//////////////////////////////////////////
			// Save / raw JSON                      //
			//////////////////////////////////////////
			SaveChanges()
			{
				this.loading = true;
				SetGraphiteAdminData(this.serializeConfigJson())
					.then(data =>
					{
						if (data.success)
						{
							toaster.success("Graphite configuration saved.");
							this.importConfigJson(data.json);
						}
						else
							toaster.error(data.error);
					})
					.catch(err =>
					{
						toaster.error(err.message);
					})
					.finally(() =>
					{
						this.loading = false;
					});
			},
			DiscardChanges()
			{
				ModalConfirmDialog("Discard all unsaved changes?", "Discard Changes?", "Discard", "Cancel")
					.then(userClickedYes =>
					{
						if (userClickedYes)
							this.loadConfig();
					});
			},
			OpenRawEditor()
			{
				AceCodeEditorDialog("GraphiteConfig.json", this.serializeConfigJson(), "ace/mode/json", (updatedJson) =>
				{
					return new Promise(resolve =>
					{
						try
						{
							let parsed = JSON.parse(updatedJson);
							for (let def of SECTION_DEFS)
								if (typeof parsed[def.key] !== "undefined" && !Array.isArray(parsed[def.key]))
									throw new Error(def.key + " must be an array of query strings.");
							if (typeof parsed.ServerNames !== "undefined" && !Array.isArray(parsed.ServerNames))
								throw new Error("ServerNames must be an array of strings.");
							this.importRawConfig(parsed);
							resolve(true);
						}
						catch (ex)
						{
							toaster.error("Invalid configuration: " + ex.message);
							resolve(false);
						}
					});
				}, null, null);
			},
			/** Imports a parsed config object from the raw JSON editor without resetting the change baseline (Save Changes is still required). */
			importRawConfig(config)
			{
				this.serverNames = Array.isArray(config.ServerNames) ? config.ServerNames.map(String) : [];
				for (let section of this.sections)
				{
					section.graphs = (Array.isArray(config[section.key]) ? config[section.key] : []).map(qs => ({
						uid: ++this.uidCounter,
						model: ParseRenderQueryString(String(qs))
					}));
				}
				if (!this.previewServer || this.serverNames.indexOf(this.previewServer) === -1)
					this.previewServer = this.serverNames.length ? this.serverNames[0] : "";
			}
		}
	}
</script>

<style scoped>
	.pageRoot
	{
		margin: 8px;
	}

	.loading
	{
		margin-top: 80px;
		text-align: center;
	}

	.error
	{
		color: #FF0000;
		font-weight: bold;
	}

	.toolbar
	{
		display: flex;
		align-items: center;
		gap: 8px;
		position: sticky;
		top: 0px;
		background-color: rgba(255,255,255,0.95);
		padding: 6px 0px;
		z-index: 10;
		border-bottom: 1px solid #DDDDDD;
	}

	.saveButton
	{
		font-weight: bold;
	}

	.unsavedNote
	{
		color: #B36B00;
		font-weight: bold;
	}

	.pageHelp
	{
		font-size: 13px;
		color: #444444;
		max-width: 900px;
	}

	.graphSection
	{
		margin-bottom: 28px;
	}

		.graphSection h3
		{
			margin-bottom: 2px;
			border-bottom: 2px solid var(--primary-color, #1A6999);
			padding-bottom: 2px;
		}

	.sectionHelp
	{
		font-size: 12px;
		color: #555555;
		margin-top: 4px;
	}

	.serverNamesEditor
	{
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 6px;
		margin-bottom: 10px;
	}

	.serverNamesLabel
	{
		font-weight: bold;
	}

	.serverChip
	{
		display: inline-flex;
		align-items: center;
		gap: 5px;
		background-color: #E8F0F7;
		border: 1px solid #A9C6DE;
		border-radius: 12px;
		padding: 2px 8px;
		font-family: Consolas, monospace;
		font-size: 13px;
	}

	.chipRemove
	{
		cursor: pointer;
		color: #AA0000;
		font-size: 11px;
	}

	.serverNameInput
	{
		width: 120px;
		padding: 2px 5px;
	}

	.previewServerLabel
	{
		margin-left: 16px;
		font-size: 13px;
	}

	.cardGrid
	{
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		align-items: flex-start;
	}

	.graphCard
	{
		border: 1px solid #B5B5B5;
		border-radius: 5px;
		background-color: #FFFFFF;
		width: 396px;
		box-shadow: 1px 1px 3px rgba(0,0,0,0.15);
	}

	.cardHeader
	{
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 3px 6px;
		background-color: #F2F2F2;
		border-bottom: 1px solid #DDDDDD;
		border-radius: 5px 5px 0px 0px;
	}

	.dragHandle
	{
		cursor: grab;
		font-size: 15px;
		color: #666666;
		user-select: none;
	}

	.cardTitle
	{
		font-size: 12px;
		font-weight: bold;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		min-width: 0px;
	}

	.cardThumb
	{
		display: block;
		width: 380px;
		height: 190px;
		margin: 8px auto 0px auto;
		cursor: pointer;
		background-color: #FAFAFA;
	}

	.cardButtons
	{
		padding: 6px;
		display: flex;
		gap: 6px;
		justify-content: flex-end;
	}

	.emptySection
	{
		color: #777777;
		font-style: italic;
		margin: 8px 0px;
	}

	.sectionButtons
	{
		margin-top: 10px;
	}
</style>
